import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { useAuthContext } from '../../contexts/AuthProvider';
import { useNavbarContext } from '../../contexts/NavbarProvider';
import { useSessionContext } from '../../contexts/SessionProvider';
import { ParticipantData } from '../../types/session';
import { showFullName, showPicture } from '../../utils/commonFunctions';

const LocalVideoStream = ({ localUser }: { localUser: ParticipantData }) => {
  // integration of react hooks here
  // const [showAllowModal, setShowAllowModal] = useState<boolean>(false);
  const [isLocalScreenStreamCaptured, setIsLocalScreenStreamCaptured] =
    useState<boolean>(false);

  // integration of context hooks here
  const {
    isCameraEnabled,
    localStream,
    localStreamRef,
    localScreenStream,
    localScreenStreamRef,
    remoteStream,
    mutedStatus,
  } = useSessionContext();

  const { isMobile } = useNavbarContext();
  const { currentUserType } = useAuthContext();

  // showing allow modal when client joins the lobby here
  // useEffect(() => {
  //   if (
  //     joinedClient &&
  //     mutedStatus.is_participant_allow === 'pending' &&
  //     currentUserType === 'user'
  //   ) {
  //     setShowAllowModal(true);
  //   }
  // }, [joinedClient, mutedStatus.is_participant_allow, currentUserType]);

  // setting the local streams in local video ref here
  useEffect(() => {
    if (localStreamRef.current) {
      localStreamRef.current.srcObject = localStream;
    }
  }, [localStream, localStreamRef]);

  // setting the local screen streams in local video ref here
  useEffect(() => {
    if (localScreenStreamRef.current) {
      localScreenStreamRef.current.srcObject = localScreenStream;
    }
  }, [localScreenStream, localScreenStreamRef]);

  // playing user screen share on/off audio sound here
  useEffect(() => {
    if (localScreenStream) {
      setIsLocalScreenStreamCaptured(true);

      const screenShareAudio = new Audio('/assets/audio/screen-share.wav');

      screenShareAudio.play();
    }

    if (isLocalScreenStreamCaptured && localScreenStream === null) {
      setIsLocalScreenStreamCaptured(false);

      const screenShareOffAudio = new Audio(
        '/assets/audio/screen-share-off.wav'
      );

      screenShareOffAudio.play();
    }
  }, [localScreenStream, isLocalScreenStreamCaptured]);

  console.log('LOCAL USER: ', localUser);
  console.log('REMOTE STREAM: ', remoteStream);
  console.log('LOCAL STREAM: ', localStream);
  console.log(
    'LOCAL SCREEN STREAM: ',
    localScreenStream,
    localScreenStreamRef.current
  );

  // rendering local video stream component here
  return (
    <>
      {localScreenStream ? (
        <div className='absolute top-0 left-[10px] md:top-5 md:left-5 h-[80px] w-[160px] md:h-[120px] md:w-[240px] rounded-lg overflow-hidden opacity-40 hover:opacity-100 duration-300 z-30'>
          <video
            ref={localScreenStreamRef}
            className='size-full object-cover'
            muted
            autoPlay
          ></video>

          <div className='absolute left-2 bottom-2 bg-base-black/50 text-base-white font-medium text-sm px-2 rounded-md z-10'>
            <p>Your Screen</p>
          </div>
        </div>
      ) : null}

      <motion.div
        initial={{ height: '100%', width: '100%' }}
        animate={
          remoteStream
            ? isMobile
              ? {
                  height: 100,
                  width: 200,
                  right: 10,
                  top: 'calc(100% - 200px)',
                  borderRadius: '8px',
                }
              : {
                  height: 120,
                  width: 240,
                  right: 20,
                  top: 'calc(100% - 140px)',
                  borderRadius: '8px',
                }
            : {
                height: '100%',
                width: '100%',
                right: 0,
                top: 0,
                borderRadius: 0,
              }
        }
        className='absolute overflow-hidden'
      >
        <video
          ref={localStreamRef}
          className='absolute top-0 left-0 min-w-full min-h-full object-cover xl:object-contain'
          muted
          autoPlay
        ></video>

        <motion.div
          initial={{ opacity: 0, display: 'none' }}
          animate={
            !isCameraEnabled || !localStream?.getVideoTracks().length
              ? { opacity: 1, display: 'flex' }
              : { opacity: 0, transitionEnd: { display: 'none' } }
          }
          className={`absolute left-0 top-0 h-full w-full items-center justify-center ${
            mutedStatus.is_associate_video_muted &&
            mutedStatus.is_client_video_muted
              ? 'bg-base-dark-gray'
              : 'bg-base-black'
          }`}
        >
          {localUser?.associate_profile_picture ? (
            <div
              className={`${
                remoteStream ? 'size-16' : 'size-32'
              } rounded-full overflow-hidden`}
            >
              <img
                className='size-full object-cover'
                src={showPicture(localUser.associate_profile_picture)}
                alt={`avatar of ${showFullName(
                  localUser.associate_first_name as string,
                  localUser.associate_middle_name,
                  localUser.associate_last_name as string
                )}`}
              />
            </div>
          ) : localUser?.client_profile_picture ? (
            <div
              className={`${
                remoteStream ? 'size-16' : 'size-32'
              } rounded-full overflow-hidden`}
            >
              <img
                className='size-full object-cover'
                src={showPicture(localUser.client_profile_picture)}
                alt={`avatar of ${showFullName(
                  localUser.associate_first_name as string,
                  localUser.associate_middle_name,
                  localUser.associate_last_name as string
                )}`}
              />
            </div>
          ) : (
            <div
              className={`bg-base-light-gray/60 rounded-full flex items-center justify-center ${
                remoteStream ? 'h-16 w-16' : 'h-32 w-32'
              }`}
            >
              <FaUser
                className={`${
                  remoteStream ? 'h-8 w-8' : 'h-20 w-20'
                } text-base-black`}
              />
            </div>
          )}
        </motion.div>

        {localUser?.associate_first_name || localUser?.client_first_name ? (
          <div className='absolute left-2 bottom-2 bg-base-black/50 text-base-white font-medium text-sm px-2 rounded-md z-10'>
            {currentUserType === 'user' ? (
              <p>
                {showFullName(
                  localUser.associate_first_name as string,
                  localUser?.associate_middle_name as string,
                  localUser?.associate_last_name as string
                )}
              </p>
            ) : (
              <p>
                {showFullName(
                  localUser.client_first_name,
                  localUser?.client_middle_name as string,
                  localUser?.client_last_name as string
                )}
              </p>
            )}
          </div>
        ) : null}
      </motion.div>

      {/* <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showAllowModal && joinedClient && (
          <ConsentModal
            titleFirstPart='Allow'
            titleSecondPart='Client?'
            subTitle={
              <>
                <span className='font-medium text-base-light-blue'>
                  {showFullName(
                    joinedClient.client_first_name,
                    joinedClient.client_middle_name,
                    joinedClient.client_last_name
                  )}
                </span>{' '}
                wants to join the session. would you like to allow this client?
              </>
            }
            isLoading={false}
            yesBtnTxt='Yes'
            yesBtnVariant='success'
            noBtnTxt='No'
            noBtnVariant='danger'
            mutationHandler={() => allowRejectClientHandler('confirmed')}
            handleModalClose={() => allowRejectClientHandler('rejected')}
          />
        )}
      </AnimatePresence> */}
    </>
  );
};

export default LocalVideoStream;
